/**
 * Looking up the recipe behind a planned meal that arrived without one (#777).
 *
 * A meal-plan week in Firestore carries only `recipeId`, so every boot, every
 * live-sync snapshot (the echo of our own writes included) and every household
 * re-subscribe asks for the recipes of content-less meals. That used to be one
 * `getRecipeById` per meal across all 36 weeks, with no dedupe and no memory of
 * a miss — `recipeService` caches hits only. Four deleted recipes in the
 * founder household were asked for ~30 times in 10 ms on one boot and fired
 * `recipe_rehydrate_failed` ~510 times in 10 days.
 *
 * Three rules now:
 *
 * - **One lookup per id at a time.** Concurrent passes share the request.
 * - **A miss is remembered for the session, per scope.** The scope is part of
 *   the key because a miss read before the household id resolved is a miss in
 *   the wrong collection; the household pass must still ask.
 * - **A meal is removed only on the server's word.** A confirmed miss is put to
 *   `confirmDeletedRecipes`, which asks the server whether the recipe is gone
 *   for every member of the plan; the caller removes the meals it confirms.
 * - **Old meals are left alone.** A meal more than `REHYDRATE_LOOKBACK_DAYS`
 *   in the past is not looked up. Nothing needs its content: the same-meal
 *   rules read 14 days back, and a card's title comes from the recipe index.
 *
 * `recipeService.getRecipeById` reports a failed read as `null`, the same as a
 * missing document, so a miss is not remembered while the browser says it is
 * offline: that "miss" is a network failure and the next pass should retry.
 */
import type { Recipe } from '../types/recipe';
import recipeService from './recipeService';
import { getActiveHouseholdId } from './householdContext';
import { addDays, todayIso } from '../utils/localDate';
import { apiPost } from './apiClient';

/**
 * How far back a meal still gets its recipe looked up. Must cover
 * `SAME_MEAL_LOOKBACK_DAYS` (`mealIdentity.ts`): the generators build their
 * same-meal history from the store's meals, and a meal with no recipe is
 * invisible to it. `recipeRehydration.test.ts` pins the two together.
 */
export const REHYDRATE_LOOKBACK_DAYS = 14;

/** The fields of a planned meal this module reads. */
export interface RehydratableMeal {
  id: string;
  recipeId: string;
  scheduledFor: string;
}

export interface RehydrationTarget {
  weekStart: string;
  mealId: string;
}

/**
 * The recipe ids a pass should look up, each with the meals waiting on it.
 * `needsRecipe` decides which meals qualify (no recipe, a content-less stub,
 * not free text); meals older than the lookback are dropped here.
 */
export function collectRehydrationTargets<M extends RehydratableMeal>(
  weeks: Record<string, M[]>,
  needsRecipe: (meal: M) => boolean,
  today: string = todayIso(),
): Map<string, RehydrationTarget[]> {
  const oldest = addDays(today, -REHYDRATE_LOOKBACK_DAYS);
  const targets = new Map<string, RehydrationTarget[]>();
  for (const [weekStart, meals] of Object.entries(weeks)) {
    for (const meal of meals) {
      if (!meal.recipeId || !needsRecipe(meal)) continue;
      // scheduledFor is a calendar day (`YYYY-MM-DD`); compare it as one.
      if (meal.scheduledFor && meal.scheduledFor.slice(0, 10) < oldest) continue;
      const waiting = targets.get(meal.recipeId);
      const target = { weekStart, mealId: meal.id };
      if (waiting) waiting.push(target);
      else targets.set(meal.recipeId, [target]);
    }
  }
  return targets;
}

const confirmedMisses = new Set<string>();
const reportedMisses = new Set<string>();
const inFlight = new Map<string, Promise<Recipe | null>>();

function scopeKey(recipeId: string, userId: string | undefined): string {
  return `${userId ?? 'guest'}|${getActiveHouseholdId() ?? 'personal'}|${recipeId}`;
}

function browserSaysOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** True when `recipeId` was already confirmed missing in this scope this session. */
export function isConfirmedMiss(recipeId: string, userId: string | undefined): boolean {
  return confirmedMisses.has(scopeKey(recipeId, userId));
}

/**
 * The recipe behind `recipeId`, or null. A miss already confirmed in this
 * scope answers null without a request; a lookup already running is shared.
 */
export function lookupPlannedRecipe(
  recipeId: string,
  userId: string | undefined,
): Promise<Recipe | null> {
  const key = scopeKey(recipeId, userId);
  if (confirmedMisses.has(key)) return Promise.resolve(null);
  const running = inFlight.get(key);
  if (running) return running;

  const lookup = recipeService
    .getRecipeById(recipeId, userId)
    .then((recipe) => {
      if (!recipe && !browserSaysOffline()) {
        confirmedMisses.add(key);
        console.warn(`  ⚠️ Could not rehydrate recipe: ${recipeId}`);
      }
      return recipe;
    })
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, lookup);
  return lookup;
}

/**
 * True the first time a confirmed miss for `recipeId` is reported in this
 * scope this session, false after — and false for an unconfirmed one (read
 * while offline). Gates `recipe_rehydrate_failed` to one event per id.
 */
export function claimMissReport(recipeId: string, userId: string | undefined): boolean {
  const key = scopeKey(recipeId, userId);
  if (!confirmedMisses.has(key) || reportedMisses.has(key)) return false;
  reportedMisses.add(key);
  return true;
}

const askedServer = new Set<string>();

/**
 * Of the ids this device just confirmed missing, the ones the server says are
 * gone for every member of the plan — the only answer a meal is removed on.
 *
 * A device cannot see a partner's personal recipes (`users/{uid}/savedRecipes`
 * is owner-only), so its own miss proves nothing about a dinner the partner
 * planned. `POST /api/household/missing-recipes` reads every place with the
 * Admin SDK (api/_lib/missingRecipes.ts). Each id is asked once per scope per
 * session. Any failure — offline, a 500, Expo's 200 + HTML for a route the dev
 * server lacks — answers "none", so nothing is removed.
 */
export async function confirmDeletedRecipes(
  recipeIds: string[],
  userId: string | undefined,
): Promise<string[]> {
  if (!userId || browserSaysOffline()) return [];
  const ask = recipeIds.filter((id) => {
    const key = scopeKey(id, userId);
    if (!confirmedMisses.has(key) || askedServer.has(key)) return false;
    askedServer.add(key);
    return true;
  });
  if (ask.length === 0) return [];
  try {
    const answer = await apiPost<{ missing?: unknown }>('/api/household/missing-recipes', {
      recipeIds: ask,
    });
    const asked = new Set(ask);
    return Array.isArray(answer?.missing)
      ? answer.missing.filter((id): id is string => typeof id === 'string' && asked.has(id))
      : [];
  } catch (error) {
    // Still asked: this runs on every snapshot, and a server that failed once
    // is not asked again until the next session.
    console.warn('Could not confirm deleted recipes:', error instanceof Error ? error.message : error);
    return [];
  }
}

/** Tests only: forget every miss, every running lookup and every question asked. */
export function resetRecipeRehydrationCache(): void {
  confirmedMisses.clear();
  reportedMisses.clear();
  inFlight.clear();
  askedServer.clear();
}
